import React, { useEffect, useState } from 'react';
import emptyCartImg from "../images/EmptyCart.png";
import { MdDelete } from "react-icons/md";
import { Link, useNavigate } from 'react-router-dom';
import { IoBagCheckSharp } from "react-icons/io5";
import { useDispatch, useSelector } from 'react-redux';
import { getUserCart, syncLocalCart } from '../features/user/userSlice';
import { base_url } from '../utils/axiosConfig';
import axios from 'axios';
import { IconButton, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LuShoppingCart } from "react-icons/lu";
import toast, { Toaster } from 'react-hot-toast';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const showToast = (type, message, subtext) => {
  const isSuccess = type === 'success';
  toast.custom((t) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: '#fff',
        border: `1px solid ${isSuccess ? '#d1fae5' : '#fee2e2'}`,
        borderLeft: `4px solid ${isSuccess ? '#10b981' : '#ef4444'}`,
        borderRadius: '12px',
        padding: '12px 16px',
        minWidth: '260px',
        maxWidth: '340px',
        opacity: t.visible ? 1 : 0,
        transform: t.visible ? 'translateY(0)' : 'translateY(-12px)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
      }}
      onClick={() => toast.dismiss(t.id)}
    >
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: isSuccess ? '#d1fae5' : '#fee2e2',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        {isSuccess
          ? <FaCheckCircle color="#10b981" size={18} />
          : <FaTimesCircle color="#ef4444" size={18} />}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '14px', color: '#111', lineHeight: 1.3 }}>{message}</div>
        {subtext && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 2 }}>{subtext}</div>}
      </div>
    </div>
  ), { duration: 2500 });
};

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartProducts, isAuthenticated } = useSelector(state => state.auth);
  const [cartItems, setCartItems] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [units, setUnits] = useState([]);

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await axios.get(`${base_url}units`);
        setUnits(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.warn("Failed to fetch units:", err);
      }
    };
    fetchUnits();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserCart());
    } else {
      const savedCart = JSON.parse(localStorage.getItem('cart')) || [];
      setCartItems(savedCart);
      dispatch(syncLocalCart(savedCart));
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && cartProducts?.length > 0) {
      const updatedCart = cartProducts.map(item => ({
        ...item,
        quantity: item.quantity || 1,
      }));
      setCartItems(updatedCart);
    } else {
      const savedCart = JSON.parse(localStorage.getItem('cart')) || [];
      const updatedSavedCart = savedCart.map(item => ({
        ...item,
        quantity: item.quantity || 1,
      }));
      setCartItems(updatedSavedCart);
    }
  }, [cartProducts, isAuthenticated]);

  const getPrice = (item) => {
    if (item.price) return item.price;
    const product = item?.ProductId;
    if (product?.variants && (item.variantId || item.variant || item.measurementUnit)) {
      const variant = product.variants.find(v =>
        v._id === item.variantId ||
        v.measurementUnit === (item.variant || item.measurementUnit)
      );
      if (variant) return variant.offerPrice || variant.CurrentPrice || 0;
    }
    return product?.price || 0;
  };

  const getOriginalPrice = (item) => {
    const product = item?.ProductId;
    if (!product) return item.price || 0;

    // If item has variant info, find it in product.variants to get its CurrentPrice (MRP)
    if (product.variants && (item.variantId || item.variant || item.measurementUnit)) {
      const variant = product.variants.find(v =>
        v._id === item.variantId ||
        v.measurementUnit === (item.variant || item.measurementUnit)
      );
      if (variant && variant.CurrentPrice) return variant.CurrentPrice;
    }

    return product.originalPrice || product.CurrentPrice || item.price || 0;
  };

  const getGstPercent = (item) => {
    return parseFloat(item.gst || item.ProductId?.gst || '0');
  };

  const increaseQuantity = async (cartItemId) => {
    const cartItem = cartItems.find(item => item._id === cartItemId);
    if (!cartItem) return;

    const newQuantity = cartItem.quantity + 1;

    if (isAuthenticated) {
      try {
        const token = JSON.parse(localStorage.getItem('customer'))?.token;
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        };

        await axios.put(`${base_url}user/updatecart`, {
          cartItemId: cartItem._id,
          ProductId: cartItem.ProductId?._id,
          variantId: cartItem.variantId,
          variant: cartItem.variant || cartItem.measurementUnit,
          quantity: newQuantity,
          price: getPrice(cartItem),
        }, config);

        const updatedCart = cartItems.map(item =>
          item._id === cartItemId ? { ...item, quantity: newQuantity } : item
        );
        setCartItems(updatedCart);
        dispatch(getUserCart());
      } catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || 'Failed to update quantity';
        showToast('error', 'Update Failed', errorMsg);
        alert(`Error: ${errorMsg}`);
      }
    } else {
      const updatedCart = cartItems.map(item =>
        item._id === cartItemId ? { ...item, quantity: newQuantity } : item
      );
      setCartItems(updatedCart);
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      dispatch(syncLocalCart(updatedCart));
      toast.success('Quantity increased (local)!');
    }
  };

  const decreaseQuantity = async (cartItemId) => {
    const cartItem = cartItems.find(item => item._id === cartItemId);
    if (!cartItem) return;

    const newQuantity = Math.max(1, cartItem.quantity - 1);

    if (isAuthenticated) {
      try {
        const token = JSON.parse(localStorage.getItem('customer'))?.token;
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        };

        await axios.put(`${base_url}user/updatecart`, {
          cartItemId: cartItem._id,
          ProductId: cartItem.ProductId?._id,
          variantId: cartItem.variantId,
          variant: cartItem.variant || cartItem.measurementUnit,
          quantity: newQuantity,
          price: getPrice(cartItem),
        }, config);

        const updatedCart = cartItems.map(item =>
          item._id === cartItemId ? { ...item, quantity: newQuantity } : item
        );
        setCartItems(updatedCart);
        dispatch(getUserCart());
      } catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || 'Failed to update quantity';
        showToast('error', 'Update Failed', errorMsg);
        alert(`Error: ${errorMsg}`);
      }
    } else {
      const updatedCart = cartItems.map(item =>
        item._id === cartItemId ? { ...item, quantity: newQuantity } : item
      );
      setCartItems(updatedCart);
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      dispatch(syncLocalCart(updatedCart));
      toast.success('Quantity decreased (local)!');
    }
  };

  const handleRemoveItem = async (cartItemId) => {
    if (isAuthenticated) {
      try {
        const token = JSON.parse(localStorage.getItem('customer'))?.token;
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        };

        await axios.delete(`${base_url}user/delete-product-cart`, {
          ...config,
          data: { cartItemId },
        });

        const updatedCart = cartItems.filter(item => item._id !== cartItemId);
        setCartItems(updatedCart);
        showToast('success', 'Item removed!', 'Product removed from your cart.');
        dispatch(getUserCart());
      } catch (error) {
        showToast('error', 'Failed to remove', 'Please try again.');
      }
    } else {
      const updatedCart = cartItems.filter(item => item._id !== cartItemId);
      setCartItems(updatedCart);
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      dispatch(syncLocalCart(updatedCart));
      showToast('success', 'Item removed!', 'Product removed from your cart.');
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
    toast.success('Coupon removed!');
  };

  const applyCoupon = async () => {
    if (!couponCode) return toast.error("Please enter a coupon code");
    if (cartItems.length === 0) return toast.error("Cart is empty");

    try {
      const response = await axios.get(`${base_url}master/getpromocode`);
      const coupons = response.data.data;

      const coupon = coupons.find(c => c.promoCodeName.toLowerCase() === couponCode.toLowerCase());
      if (!coupon) return toast.error("Invalid coupon code");

      // Check expiry date
      const today = new Date();
      const validUntil = new Date(coupon.validate);
      if (today > validUntil) return toast.error("Coupon expired");

      const cartTotal = parseFloat(calculateCartTotal());
      const discountPercent = parseFloat(coupon.offer.replace('%', ''));
      const discountAmount = ((cartTotal * discountPercent) / 100).toFixed(2);

      setAppliedCoupon(coupon);
      setCouponError('');
      toast.success(`Coupon applied! You saved ₹${discountAmount} (${discountPercent}%)`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to apply coupon");
    }
  };

  const calculateCartTotal = () => {
    return cartItems
      .reduce((acc, item) => {
        const price = getPrice(item);
        return acc + price * item.quantity;
      }, 0)
      .toFixed(2);
  };

  const calculateSubTotal = () => {
    return cartItems
      .reduce((acc, item) => {
        const price = getPrice(item);
        const gstPercent = getGstPercent(item);
        const gstAmount = (price * item.quantity * gstPercent) / 100;
        return acc + price * item.quantity + gstAmount;
      }, 0)
      .toFixed(2);
  };

  const calculateOriginalTotal = () => {
    return cartItems
      .reduce((acc, item) => {
        const origPrice = getOriginalPrice(item);
        return acc + origPrice * item.quantity;
      }, 0)
      .toFixed(2);
  };

  const calculateDiscount = () => {
    const origTotal = parseFloat(calculateOriginalTotal());
    const cartTotal = parseFloat(calculateCartTotal());
    return (origTotal - cartTotal).toFixed(2);
  };

  const calculateCouponDiscount = () => {
    if (!appliedCoupon) return 0;
    const cartTotal = parseFloat(calculateCartTotal());
    const discountPercent = parseFloat(appliedCoupon.offer) || 0;
    return ((cartTotal * discountPercent) / 100).toFixed(2);
  };

  const calculateTotalWithCoupon = () => {
    const subTotal = parseFloat(calculateSubTotal());
    const couponDiscount = parseFloat(calculateCouponDiscount());
    return (subTotal - couponDiscount).toFixed(2);
  };

  const calculateSavings = () => {
    const discount = parseFloat(calculateDiscount());
    const couponDiscount = parseFloat(calculateCouponDiscount());
    return (discount + couponDiscount).toFixed(2);
  };

  return (
    <section className="cart-wrapper py-4 min-vh-100">
      <Toaster position="top-center" reverseOrder={false} />
      <div className="container">
        {cartItems.length === 0 ? (
          <div className="empty-cart-container">
            <div className="empty-cart-content text-center p-5 rounded-4 shadow-sm bg-white">
              <img
                src={emptyCartImg}
                alt="Empty Cart"
                className="mb-4"
                style={{ width: '160px', opacity: 0.8 }}
              />
              <h3 className="fw-bold mb-2">Your cart is feeling light</h3>
              <p className="text-muted mb-4">Seems like you haven't added anything to your cart yet.</p>
              <Link to="/product" className="btn btn-primary px-5 py-2 rounded-pill">
                Explore Products
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="page-header d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center gap-3">
                <IconButton onClick={() => navigate(-1)} className="back-btn shadow-sm bg-white">
                  <ArrowBackIcon />
                </IconButton>
                <div>
                  <h4 className="fw-bold mb-0">My Shopping Cart</h4>
                  <p className="text-muted mb-0 x-small">{cartItems.length} items ready</p>
                </div>
              </div>
            </div>

            <div className="row g-4">
              {/* Cart Items List */}
              <div className="col-lg-8">
                <div className="cart-items-container">
                  {cartItems.map((item, index) => {
                    const price = getPrice(item);
                    const originalPrice = getOriginalPrice(item);
                    const gstPercent = getGstPercent(item);
                    const gstAmount = (price * item.quantity * gstPercent) / 100;
                    const totalWithGst = (price * item.quantity + gstAmount).toFixed(2);
                    const discountPercent =
                      originalPrice > price ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;

                    // 🔹 Find matching variant to get its unit title and stock limit
                    const product = item?.ProductId;
                    let variantUnitTitle = "";
                    let variantMaxStock = 999;
                    if (product?.variants && (item.variantId || item.variant || item.measurementUnit)) {
                      const matchedVariant = product.variants.find(v =>
                        String(v._id) === String(item.variantId) ||
                        String(v.measurementUnit).trim() === String(item.variant || item.measurementUnit || "").trim()
                      );
                      if (matchedVariant) {
                        const unitVal = matchedVariant.unit;
                        const unitId = typeof unitVal === 'object' ? unitVal?._id : unitVal;
                        const unitObj = units.find(u => String(u._id) === String(unitId));
                        variantUnitTitle = matchedVariant.title || (typeof unitVal === 'object' && unitVal?.title) || unitObj?.title || "";
                        variantMaxStock = matchedVariant.quantity !== undefined && matchedVariant.quantity !== null ? matchedVariant.quantity : 999;
                      }
                    }

                    if (!variantUnitTitle && item?.unitTitle) {
                      variantUnitTitle = item.unitTitle;
                    }

                    return (
                      <div key={index} className="cart-item-modern mb-2 p-2 bg-white rounded-3 shadow-sm border-0 position-relative overflow-hidden">
                        <div className="row align-items-center g-2">
                          <div className="col-auto">
                            <div className="product-img-wrapper rounded-3 overflow-hidden border">
                              <img
                                src={`${base_url}${item?.ProductId?.images?.[0]?.url}`}
                                alt={item?.ProductId?.title}
                                className="img-fluid"
                                style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                              />
                            </div>
                          </div>

                          <div className="col">
                            <div className="d-flex flex-column h-100">
                              <div className="d-flex justify-content-between align-items-start mb-1">
                                <h6 className="item-title fw-bold text-dark mb-0 pe-4" style={{ fontSize: '0.9rem' }}>
                                  {item?.ProductId?.title || "Unknown Product"}
                                </h6>
                                <IconButton
                                  onClick={() => handleRemoveItem(item._id)}
                                  className="remove-icon-btn text-danger bg-light"
                                  size="small"
                                >
                                  <MdDelete size={18} />
                                </IconButton>
                              </div>

                              {(item?.variant || item?.measurementUnit) && (
                                <span className="badge bg-light text-secondary border mb-1 align-self-start py-1 px-2" style={{ fontSize: '10px' }}>
                                  {item.variant || item.measurementUnit} {variantUnitTitle}
                                </span>
                              )}

                              <div className="item-pricing mb-1">
                                <div className="d-flex align-items-center gap-2">
                                  <span className="fs-6 fw-bold text-primary">₹{price?.toLocaleString('en-IN')}</span>
                                  {originalPrice > price && (
                                    <>
                                      <span className="text-muted text-decoration-line-through x-small">₹{originalPrice?.toLocaleString('en-IN')}</span>
                                      <span className="badge bg-success-subtle text-success x-small" style={{ fontSize: '9px' }}>{discountPercent}% OFF</span>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="d-flex align-items-center justify-content-between mt-1">
                                <div className="d-flex align-items-center">
                                  <div className="modern-stepper d-flex align-items-center bg-light rounded-pill p-0 border">
                                    <IconButton
                                      onClick={() => decreaseQuantity(item._id)}
                                      disabled={item.quantity <= 1}
                                      size="small"
                                      className="stepper-btn bg-white shadow-sm"
                                      style={{ width: '24px', height: '24px' }}
                                    >
                                      <RemoveIcon style={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                    <span className="px-2 fw-bold" style={{ fontSize: '0.85rem' }}>{item?.quantity || 1}</span>
                                    <IconButton
                                      onClick={() => increaseQuantity(item._id)}
                                      disabled={item.quantity >= variantMaxStock}
                                      size="small"
                                      className="stepper-btn bg-white shadow-sm"
                                      style={{ width: '24px', height: '24px' }}
                                    >
                                      <AddIcon style={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                  </div>
                                  {item.quantity >= variantMaxStock && (
                                    <span className="text-danger ms-2 fw-semibold" style={{ fontSize: '10px' }}>
                                      Out of stock
                                    </span>
                                  )}
                                </div>

                                <div className="text-end">
                                  <small className="text-muted d-block" style={{ fontSize: '9px' }}>SUBTOTAL</small>
                                  <span className="fw-bold" style={{ fontSize: '0.9rem' }}>₹{totalWithGst}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Summary Sidebar */}
              <div className="col-lg-4">
                <div className="summary-card-modern p-3 bg-white rounded-4 shadow-sm border-0 sticky-top" style={{ top: '100px' }}>
                  <h6 className="fw-bold mb-3 d-flex align-items-center">
                    <span className="bg-primary-subtle p-2 rounded-3 me-2">
                      <LuShoppingCart className="text-primary" size={16} />
                    </span>
                    Order Summary
                  </h6>

                  {/* Coupon Section */}
                  <div className="coupon-box p-2 bg-light rounded-3 mb-3 border">
                    <label className="form-label x-small fw-bold text-muted text-uppercase mb-1">Coupon</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control border-0 bg-white"
                        placeholder="Code"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                        disabled={!!appliedCoupon}
                        style={{ fontSize: '12px' }}
                      />
                      {appliedCoupon ? (
                        <button className="btn btn-danger btn-sm px-2" onClick={removeCoupon} style={{ fontSize: '11px' }}>Remove</button>
                      ) : (
                        <button className="btn btn-primary btn-sm px-2" onClick={applyCoupon} disabled={!couponCode} style={{ fontSize: '11px' }}>Apply</button>
                      )}
                    </div>
                  </div>

                  {/* Price Breakdown */}
                  <div className="price-details-modern mb-3" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Original</span>
                      <span>₹{parseFloat(calculateOriginalTotal()).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Discount</span>
                      <span className="text-success fw-medium">-₹{parseFloat(calculateDiscount()).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">GST</span>
                      <span>₹{(parseFloat(calculateSubTotal()) - parseFloat(calculateCartTotal())).toFixed(2)}</span>
                    </div>
                    {appliedCoupon && (
                      <div className="d-flex justify-content-between mb-1 text-primary fw-medium">
                        <span>Coupon</span>
                        <span>-₹{parseFloat(calculateCouponDiscount()).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <hr className="my-2 opacity-10" />
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">Total Payable</span>
                      <span className="fw-bold fs-5 text-primary">₹{parseFloat(calculateTotalWithCoupon()).toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  {parseFloat(calculateSavings()) > 0 && (
                    <div className="savings-banner alert alert-success border-0 mb-3 py-1 px-2 rounded-2 d-flex align-items-center gap-2">
                      <div className="small">
                        You save <strong>₹{parseFloat(calculateSavings()).toLocaleString('en-IN')}</strong>!
                      </div>
                    </div>
                  )}

                  <Link
                    to="/checkout"
                    onClick={() => localStorage.setItem("checkoutTotal", parseFloat(calculateTotalWithCoupon()))}
                    className="btn btn-primary w-100 py-2 rounded-3 shadow-sm fw-bold d-flex align-items-center justify-content-center gap-2"
                    style={{ fontSize: '14px' }}
                  >
                    Checkout <IoBagCheckSharp size={18} />
                  </Link>

                  <div className="text-center mt-3">
                    <Link to="/product" className="text-decoration-none text-muted small hover-primary">
                      <ArrowBackIcon style={{ fontSize: '14px' }} /> Back to shopping
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .x-small { font-size: 11px; }
        .cart-wrapper {
          background-color: #f8fafc;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        
        .back-btn {
          width: 36px;
          height: 36px;
          transition: transform 0.2s ease;
        }
        
        .back-btn:hover {
          transform: translateX(-4px);
          color: #2874f0;
        }
        
        .cart-item-modern {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(0,0,0,0.05) !important;
        }
        
        .cart-item-modern:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
          border-color: rgba(40, 116, 240, 0.2) !important;
        }
        
        .product-img-wrapper {
          background-color: #fcfcfc;
          transition: transform 0.3s ease;
        }
        
        .modern-stepper {
          min-width: 80px;
        }
        
        .stepper-btn {
          padding: 0 !important;
          transition: all 0.2s ease;
        }
        
        .stepper-btn:hover {
          background-color: #f0f0f0 !important;
        }
        
        .summary-card-modern {
          border: 1px solid rgba(0,0,0,0.05) !important;
        }
        
        .coupon-box .form-control:focus {
          box-shadow: none;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #2874f0 0%, #1a5dc7 100%) !important;
          border: none !important;
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(26, 93, 199, 0.3) !important;
        }
        
        .hover-primary:hover {
          color: #2874f0 !important;
        }
        
        .badge.bg-success-subtle {
          background-color: #dcfce7 !important;
          color: #166534 !important;
        }
        
        @media (max-width: 991px) {
          .summary-card-modern {
            position: static !important;
            margin-top: 1.5rem;
          }
        }
        
        @media (max-width: 576px) {
          .cart-item-modern {
            padding: 0.75rem !important;
          }
          .item-title {
            font-size: 0.85rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Cart;